import { Schemas } from "../types/api-types";
import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export type GlAccountResponse = Schemas['GLAccountResponse'];
export type ChequeBookResponse = Schemas['ChequeBookResponse'];
export type GlCoaResponse = Schemas['GlCoaResponse'];

export async function getGlAccounts(branchId?: number, type?: string): Promise<GlAccountResponse[]> {
    try {
        const response: ApiResponse<GlAccountResponse[]> = (
            await apiClient.get('/gl_account',
                { params: { branch_id: branchId, type: type } }
            )).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response?.status === 401) {
            throw new Error('Please login again to continue');
        }
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        if (error.message) {
            throw new Error(error.message);
        }
        throw new Error('Failed to fetch GL accounts');
    }
}

export async function getChequeBooks(): Promise<ChequeBookResponse[]> {
    try {
        const response: ApiResponse<ChequeBookResponse[]> = (
            await apiClient.get('/gl_account/cheque_books')
        ).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response?.status === 401) {
            throw new Error('Please login again to continue');
        }
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        if (error.message) {
            throw new Error(error.message);
        }
        throw new Error('Failed to fetch cheque books');
    }
}

export async function getGlCoas(): Promise<GlCoaResponse[]> {
    try {
        const response: ApiResponse<GlCoaResponse[]> = (
            await apiClient.get('/gl_account/coa')
        ).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        if (error.message) {
            throw new Error(error.message);
        }
        throw new Error('Failed to fetch GL COAs');
    }
}

export interface GLAccountRead {
    id: number;
    gl_coa_id?: number;
    branch_id?: number;
    section_id?: number;
    ref_number?: string;
    budget?: number;
    status?: number;
    c_at?: string;
    m_at?: string;

    // Joined fields
    gl_coa_name?: string;
    branch_name?: string;
    section_name?: string;
    gl_coa_ref_number?: string;
}

export interface GLAccountPaginated {
    items: GLAccountRead[];
    total: number;
    page: number;
    page_size: number;
    has_more: boolean;
}

export type GLAccountCreate = {
    gl_coa_id: number;
    branch_id: number;
    ref_number: string;
    section_id?: number;
    budget?: number;
    status?: number;
};
export type GLAccountUpdate = Partial<GLAccountCreate>;

export async function getGLAccountsPaginated(filters: {
    search_term?: string;
    branch_id?: number;
    gl_coa_id?: number;
    page?: number;
    page_size?: number;
} = {}): Promise<GLAccountPaginated> {
    try {
        const response = await apiClient.get<ApiResponse<GLAccountPaginated>>('/gl_account/paginated', {
            params: { ...filters, page: filters.page || 1, page_size: filters.page_size || 10 }
        });
        return response.data.data!;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to retrieve GL Accounts');
    }
}

export async function createGLAccount(data: GLAccountCreate): Promise<GLAccountRead> {
    try {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));

        const response = await apiClient.post<ApiResponse<GLAccountRead>>('/gl_account/create', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data.data!;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to create GL Account');
    }
}

export async function updateGLAccount(id: number, data: GLAccountUpdate): Promise<GLAccountRead> {
    try {
        const response = await apiClient.post<ApiResponse<GLAccountRead>>(`/gl_account/${id}`, data);
        return response.data.data!;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to update GL Account');
    }
}

export async function getGLAccountById(id: number): Promise<GLAccountRead> {
    try {
        const response = await apiClient.get<ApiResponse<GLAccountRead>>(`/gl_account/${id}`);
        return response.data.data!;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to retrieve GL Account');
    }
}

export interface GLAccountLookups {
    branches: any[];
    gl_coas: any[];
    sections: any[];
}

export async function getGLAccountLookups(): Promise<GLAccountLookups> {
    try {
        const response = await apiClient.get<ApiResponse<GLAccountLookups>>('/gl_account/gl-account-lookups');
        return response.data.data!;
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to retrieve lookups');
    }
}

export async function getCurrentAccounts(additionalOperationFilters?: number[]): Promise<Array<{ id: number; name_ln1: string; ref_number?: string }>> {
  const response = await apiClient.get('/gl_account/current-accounts', {
    params: {
      additional_operation_filters: additionalOperationFilters,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
  
  return response.data.data;
}

export async function getAvailableGLCoas(branchId: number, sectionId?: number): Promise<{ id: number; name_ln1: string; ref_number: string }[]> {
    try {
        const params: any = { branch_id: branchId };
        if (sectionId) params.section_id = sectionId;
        const response = await apiClient.get<ApiResponse<{ id: number; name_ln1: string; ref_number: string }[]>>('/gl_account/available-coas', { params });
        return response.data.data || [];
    } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to retrieve available COAs');
    }
}